/* eslint-disable */
import { EyeOutlined } from '@ant-design/icons';
import {
  Button, Card, message, Modal, Space,
  Tooltip,
  Typography
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { AxiosError } from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import DetailModal from './detailModal';

import { useAppSelector } from 'app/store';
import PageTable from 'common/components/PageTable';
import { approveStaffProjectService, declineStaffProjectService, getStaffFromPayRallProjectService } from 'common/services/extends/project';
import { detectError } from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';
import { ApproveStaffParams } from 'common/services/extends/project/types';

export type ColumnType = {
  id: number;
  name: string;
  idCardNumber: string;
};

type WaitingForApproveStaffsProps = {
  disabledAction?: boolean;
};

const WaitForApproveStaffs: React.FC<WaitingForApproveStaffsProps> = ({
  disabledAction
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Number(searchParams.get('page'));
  const projectId = searchParams.get('projectId') || '';
  const staffFullTimeId = searchParams.get('staffFullTimeId') || '';
  const payrollPeriodId = searchParams.get('payrollPeriodId') || '';
  const queryClient = useQueryClient();
  const { roles: userRoles } = useAppSelector((state) => state.auth);
  const {
    defaultPageSize,
  } = useAppSelector((state) => state.system);

  /* States */
  const [currentPage, setCurrentPage] = useState(Number(pageParam || 1));
  const [keyword, setKeyword] = React.useState<string>('');
  const [selectedRows, setSelectedRows] = React.useState<ColumnType[]>([]);
  const [openId, setOpenId] = React.useState<number | undefined>(undefined);

  const allowApprove = selectedRows.length > 0
    && getPermission(userRoles, roles.PROJECT_APPROVED_STAFF);

  const allowReject = selectedRows.length > 0
    && getPermission(userRoles, roles.PROJECT_DECLINE_STAFF);

  const queryWaitingStaff = ['get-waiting-staffs-project', projectId, staffFullTimeId, currentPage, keyword, defaultPageSize];

  /* Query */
  const { data: staffRes, isLoading, refetch } = useQuery(
    queryWaitingStaff,
    () => {
      if (payrollPeriodId) {
        return getStaffFromPayRallProjectService(payrollPeriodId, {
          page: currentPage,
          limit: defaultPageSize,
          approved: false,
          ...keyword && { keyword },
        });
      }
      return null;
    },
    {
      enabled: !!(payrollPeriodId) && getPermission(userRoles, roles.PROJECT_INDEX),
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const formatStaff: ColumnType[] = useMemo(() => staffRes?.data.map((item) => ({
    id: item.staffData.id,
    name: item.staffData.fullname,
    idCardNumber: item.staffData.idCardNumber,
  })) || [], [staffRes]);

  const { mutate: approveMutate, isLoading: isLoadingApprove } = useMutation(
    ['approve-staffs'],
    (params: ApproveStaffParams) => approveStaffProjectService(params),
    {
      onSuccess: () => {
        message.success(t('message.approveStaffSuccess'));
        refetch();
        queryClient.invalidateQueries(['get-approved-staffs-project']);
        if (openId) setOpenId(undefined);
      },
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const { mutate: rejectMutate, isLoading: isLoadingReject } = useMutation(
    ['reject-staffs'],
    (params: ApproveStaffParams) => declineStaffProjectService(params),
    {
      onSuccess: () => {
        message.success(t('message.declineStaffSuccess'));
        queryClient.invalidateQueries(['get-waiting-staffs-project', projectId]);
        if (openId) setOpenId(undefined);
      },
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const handleApprove = () => {
    if (projectId && staffFullTimeId && selectedRows.length > 0) {
      approveMutate({
        payrollPeriodId,
        staffIds: selectedRows.map((row) => row.id),
        approved: true
      });
    }
  };

  const handleApproveSingle = (index: number) => {
    if (projectId && payrollPeriodId) {
      approveMutate({
        payrollPeriodId,
        staffIds: [index],
        approved: true
      });
    }
  };

  const handleReject = () => {
    if (projectId && payrollPeriodId && selectedRows.length > 0) {
      rejectMutate({
        payrollPeriodId,
        staffIds: selectedRows.map((row) => row.id),
      });
    }
  };

  const handleRejectSingle = (index: number) => {
    if (projectId && payrollPeriodId) {
      rejectMutate({
        payrollPeriodId,
        staffIds: [index],
      });
    }
  };

  const columns: ColumnsType<ColumnType> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: t('system.name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text, record) => (
        <Typography.Text
          style={{ cursor: 'pointer', display: 'inline-block', width: '100%' }}
          onClick={() => setOpenId(record.id)}
        >
          {text}
        </Typography.Text>
      ),
    },
    {
      title: t('program.idCardNumber'),
      dataIndex: 'idCardNumber',
      key: 'idCardNumber',
      render: (text, record) => (
        <Typography.Text
          style={{ cursor: 'pointer', display: 'inline-block', width: '100%' }}
          onClick={() => setOpenId(record.id)}
        >
          {text}
        </Typography.Text>
      ),
    },
    {
      title: t('system.action'),
      key: 'action',
      width: 100,
      align: 'center',
      render: (record) => (
        <Space>
          <Tooltip title={t('program.seeDetailEmployee')}>
            <Button
              icon={<EyeOutlined />}
              onClick={() => setOpenId(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleSetCurrentPage = (page: number) => {
    setCurrentPage(page);
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set('page', String(page));
    setSearchParams(newSearchParams);
  };

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(Number(pageParam));
    } else {
      setCurrentPage(1);
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('page', '1');
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Card
        style={{ height: '100%' }}
        title={(
          <Space size={0} style={{ width: '100%', justifyContent: 'space-between' }}>
            <Typography.Title level={4}>
              {t('program.waitForApproveStaffs')}
            </Typography.Title>
            <Space>
              <Button type="primary" disabled={!allowApprove || disabledAction} loading={isLoadingApprove} onClick={handleApprove}>
                {t('program.approveAll')}
              </Button>
              <Button
                danger
                loading={isLoadingReject}
                disabled={!allowReject || disabledAction}
                onClick={() => Modal.confirm({
                  className: 't-pagetable_deleteRecordModal',
                  autoFocusButton: 'cancel',
                  okText: t('system.ok'),
                  cancelText: t('system.cancel'),
                  cancelButtonProps: {
                    type: 'primary',
                  },
                  okButtonProps: {
                    type: 'default',
                  },
                  title: t('message.confirmDeleteRecord'),
                  onOk: () => handleReject(),
                })}
              >
                {t('program.rejectAll')}
              </Button>
            </Space>
          </Space>
        )}
      >
        <div className="p-program_waitApproveStaffs">
          <PageTable
            isLoading={isLoading}
            handleSearch={(k) => setKeyword(k)}
            roles={{
              roleUpdate: true,
              roleCreate: true,
              roleDelete: true,
            }}
            tableProps={{
              columns,
              pageData: formatStaff,
              currentPage,
              pageSize: defaultPageSize,
              total: staffRes?.meta.total,
              noBaseCol: true,
              noDeleteLanguage: true,
              initShowColumns: ['name', 'idCardNumber', 'action'],
              handleSetCurrentPage,
              scroll: {
                x: 'auto'
              }
            }}
            noCheckbox={disabledAction}
            handleSelectRows={(_, rows) => {
              setSelectedRows(rows);
            }}
          />
        </div>
      </Card>
      <DetailModal
        openId={openId}
        handleClose={() => setOpenId(undefined)}
        handleApprove={(index) => handleApproveSingle(index)}
        handleReject={(index) => handleRejectSingle(index)}
      />
    </>

  );
};

export default WaitForApproveStaffs;
